import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ValidationError } from 'yup';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cbhq/cds-web/overlays';

import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useCreateProject } from ':cloud/queries/Projects/useCreateProject';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';
import { generateProjectNameSchema } from ':cloud/utils/validate';

type ProjectCreateModalProps = Pick<ModalProps, 'visible' | 'onRequestClose'> & {
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
};

export function ProjectCreateModal({ visible, onRequestClose }: ProjectCreateModalProps) {
  const { activeOrg } = useGetUser();
  const organizationId = activeOrg?.organizationId || '';
  const { projects } = useGetProjects(organizationId);
  const createProject = useCreateProject();
  const history = useHistory();

  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const validationSchema = useMemo(() => {
    const existingProjectNames = projects.map((p) => p.name.toLowerCase());
    return generateProjectNameSchema({ value: value.trim().toLowerCase() }, existingProjectNames);
  }, [projects, value]);

  const handleOnRequestClose = useCallback(() => {
    setError('');
    setValue('');
    onRequestClose?.();
  }, [onRequestClose]);

  const handleSubmit = useCallback(() => {
    logClick(EventName.create_project_click);
    try {
      validationSchema.validateSync({ value: value.trim() });
      createProject.mutate(
        { name: value.trim(), organizationId },
        {
          onSuccess: (data) => {
            handleOnRequestClose();
            // @ts-expect-error @amanda.mcdermott maintaining backwards compatibility until COMS FE cleanup (CDPPS-228)
            const projectId = data?.id || data?.project?.id;
            history.push(`${AppRoute.Projects.Overview}?projectId=${projectId}`);
          },
        },
      );
    } catch (validationError) {
      if (validationError instanceof ValidationError) {
        setError(validationError.message);
      }
    }
  }, [value, organizationId, createProject, handleOnRequestClose, validationSchema, history]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <Modal visible={visible} onRequestClose={handleOnRequestClose} testID="project-create-modal">
      <ModalHeader title="Create new project" />
      <ModalBody>
        <TextInput
          label="Project name"
          value={value}
          placeholder="Name your project"
          onChange={handleOnChange}
          helperText={error}
          variant={error ? 'negative' : undefined}
        />
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button variant="primary" onPress={handleSubmit} loading={createProject.isLoading}>
            Create
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" onPress={handleOnRequestClose}>
            Cancel
          </Button>
        }
      />
    </Modal>
  );
}
